import { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Button, TextField, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useProject, useProjectTasks } from '../../../api/hooks/useProjects';
import { useProjectDashboard } from '../../../api/hooks/useProjectDashboard';
import { useRisiken } from '../../../api/hooks/useRisiken';
import { useOffenePunkte } from '../../../api/hooks/useOffenePunkte';
import { useUpsertReifegrad } from '../../../api/hooks/useReifegrad';
import { useNotifications } from '../../../context/NotificationContext';
import { exportToPdf } from '../../../utils/exportPdf';
import { PmCard, PmInfoBox, PmPill, PmViewToggle } from '../../../components/pm';

type Audience = 'gf' | 'lk' | 'team';
type TrafficLight = 'green' | 'yellow' | 'red';

interface TemplateDef {
  id: Audience;
  label: string;
  hint: string;
  sections: Array<{ key: string; label: string; placeholder: string; rows: number }>;
}

const TEMPLATES: TemplateDef[] = [
  {
    id: 'gf',
    label: 'GF (Geschäftsführung)',
    hint: 'Knapp, strategisch. Top-Level-Status, Eskalationsbedarf, Budget-/Zeitrahmen.',
    sections: [
      { key: 'achievements', label: 'Erreichtes', placeholder: 'Welche strategischen Meilensteine wurden erreicht?', rows: 3 },
      { key: 'plan', label: 'Geplant (nächste Periode)', placeholder: 'Was steht als nächstes an?', rows: 3 },
      { key: 'risks', label: 'Risiken & Eskalationen', placeholder: 'Welche Top-Risiken brauchen GF-Aufmerksamkeit?', rows: 3 },
      { key: 'decisions', label: 'Entscheidungsbedarf', placeholder: 'Wofür wird eine Entscheidung der GF benötigt?', rows: 3 },
    ],
  },
  {
    id: 'lk',
    label: 'Lenkungskreis',
    hint: 'Detaillierter. Fortschritt, Ressourcen, Risiken mit Mitigationsstand.',
    sections: [
      { key: 'achievements', label: 'Erreichtes', placeholder: 'Detaillierte Meilenstein- und Aktivitätsübersicht.', rows: 4 },
      { key: 'plan', label: 'Geplant', placeholder: 'Konkrete Schritte der nächsten Periode + Verantwortliche.', rows: 4 },
      { key: 'risks', label: 'Risiken (W × A × Strategie)', placeholder: 'Risiken mit Eintritts-WS, Auswirkung, Maßnahme.', rows: 4 },
      { key: 'decisions', label: 'Entscheidungsbedarf', placeholder: 'Bedarf an LK-Entscheidungen (mit Empfehlung).', rows: 3 },
      { key: 'resources', label: 'Ressourcen', placeholder: 'Personal-/Budget-Bedarf, Engpässe.', rows: 3 },
    ],
  },
  {
    id: 'team',
    label: 'Team',
    hint: 'Operativ. Tasks-Status, Sprint-Ziele, Blockaden, Action Items.',
    sections: [
      { key: 'achievements', label: 'Was lief gut', placeholder: 'Tasks erledigt, Erfolge.', rows: 3 },
      { key: 'plan', label: 'Was kommt', placeholder: 'Sprint-Ziele, kommende Tasks.', rows: 3 },
      { key: 'blockers', label: 'Blocker', placeholder: 'Was hält das Team auf?', rows: 3 },
      { key: 'actions', label: 'Action Items', placeholder: 'Konkrete To-Dos mit Owner und Deadline.', rows: 3 },
    ],
  },
];

const TRAFFIC: Record<TrafficLight, { label: string; tone: 'success' | 'warning' | 'error' }> = {
  green: { label: 'Grün – auf Kurs', tone: 'success' },
  yellow: { label: 'Gelb – Aufmerksamkeit nötig', tone: 'warning' },
  red: { label: 'Rot – Eskalation erforderlich', tone: 'error' },
};

interface DashboardData {
  tasks: { total: number; open: number; done: number };
  risks: { total: number; high: number; mitigated: number };
  offene_punkte: { open: number; closed: number };
  reifegrad_avg: number | null;
}

interface DashboardRaw {
  schritte?: Array<{ reifegrad: number }>;
}

interface TaskRow { state?: string; status?: string }
interface RisikoRow { status?: string; weight?: number; assessment?: number; beschreibung?: string; score?: number }
interface OffenerPunktRow {
  status?: string;
  titel?: string | null;
  kategorie?: string | null;
  verantwortlich?: string | null;
  faellig?: string | null;
}

interface StoredReport {
  id: string;
  savedAt: string;
  audience: Audience;
  traffic: TrafficLight;
  periodFrom: string;
  periodTo: string;
  sections: Record<string, string>;
  projectName: string;
}

const STORAGE_KEY = (pid: string) => `pm:status-reports:${pid}`;

function readStoredReports(pid: string): StoredReport[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY(pid));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredReport[];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function writeStoredReports(pid: string, reports: StoredReport[]): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(STORAGE_KEY(pid), JSON.stringify(reports)); } catch { /* quota */ }
}

const DONE_TASK_STATES = new Set(['COMPLETED', 'CANCELED', 'DONE', 'CLOSED', 'completed', 'closed', 'done']);
const HIGH_RISK_STATUSES = new Set(['Eingetreten']);
const MITIGATED_RISK_STATUSES = new Set(['Mitigiert', 'Geschlossen']);
const CLOSED_OP_STATUSES = new Set(['Erledigt', 'Geschlossen', 'Beantwortet']);

export function StatusReportTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();

  const { data: projectResp, isLoading } = useProject(projectId);
  const { data: dashResp } = useProjectDashboard(projectId);
  const { data: tasksResp } = useProjectTasks(projectId, 'flat');
  const { data: risikenResp } = useRisiken(projectId);
  const { data: opResp } = useOffenePunkte(projectId);
  const upsertReifegrad = useUpsertReifegrad(projectId);

  const project = (projectResp as unknown as { data?: { name?: string; state?: string } } | undefined)?.data ?? null;
  const dashRaw = (dashResp as unknown as { data?: DashboardRaw } | undefined)?.data ?? null;
  const tasksList = ((tasksResp as unknown as { data?: TaskRow[] } | undefined)?.data ?? []);
  const risikenList = ((risikenResp as unknown as { data?: RisikoRow[] } | undefined)?.data ?? []);
  const opList = ((opResp as unknown as { data?: OffenerPunktRow[] } | undefined)?.data ?? []);

  // Backend `/dashboard` liefert nur `schritte[]`, `weeklyProgress[]`, `miniGantt[]`, `milestones[]`,
  // `kpi{}` — keine Tasks/Risk/OP-Aggregate. Wir bauen sie hier client-side aus den Live-Endpunkten.
  const dash: DashboardData = useMemo(() => {
    const doneCount = tasksList.filter((t) => DONE_TASK_STATES.has(t.state ?? t.status ?? '')).length;
    const highCount = risikenList.filter((r) => HIGH_RISK_STATUSES.has(r.status ?? '') || ((r.weight ?? 0) * (r.assessment ?? 0)) >= 16).length;
    const mitigatedCount = risikenList.filter((r) => MITIGATED_RISK_STATUSES.has(r.status ?? '')).length;
    const opClosed = opList.filter((op) => CLOSED_OP_STATUSES.has(op.status ?? '')).length;
    const reifegrade = (dashRaw?.schritte ?? []).map((s) => s.reifegrad).filter((r) => r > 0);
    const reifegradAvg = reifegrade.length > 0 ? reifegrade.reduce((a, b) => a + b, 0) / reifegrade.length : null;
    return {
      tasks: { total: tasksList.length, done: doneCount, open: tasksList.length - doneCount },
      risks: { total: risikenList.length, high: highCount, mitigated: mitigatedCount },
      offene_punkte: { open: opList.length - opClosed, closed: opClosed },
      reifegrad_avg: reifegradAvg,
    };
  }, [tasksList, risikenList, opList, dashRaw]);

  const [audience, setAudience] = useState<Audience>('lk');
  const [traffic, setTraffic] = useState<TrafficLight>('green');
  const [periodFrom, setPeriodFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [periodTo, setPeriodTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [sections, setSections] = useState<Record<string, string>>({});
  const [exporting, setExporting] = useState(false);

  // Gespeicherte Reports (LocalStorage, DEV-only — Backend-Archiv folgt)
  const [storedReports, setStoredReports] = useState<StoredReport[]>(() => readStoredReports(projectId));
  useEffect(() => { setStoredReports(readStoredReports(projectId)); }, [projectId]);

  const template = useMemo(() => TEMPLATES.find((tp) => tp.id === audience)!, [audience]);

  const handleSaveReport = () => {
    const report: StoredReport = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      savedAt: new Date().toISOString(),
      audience,
      traffic,
      periodFrom,
      periodTo,
      sections,
      projectName: project?.name ?? 'Projekt',
    };
    const next = [report, ...storedReports];
    writeStoredReports(projectId, next);
    setStoredReports(next);
    notify(t('statusReport.saved', 'Bericht gespeichert'), 'success');
  };

  const handleLoadReport = (id: string) => {
    const report = storedReports.find((r) => r.id === id);
    if (!report) return;
    setAudience(report.audience);
    setTraffic(report.traffic);
    setPeriodFrom(report.periodFrom);
    setPeriodTo(report.periodTo);
    setSections(report.sections);
    notify(t('statusReport.loaded', 'Bericht geladen'), 'info');
  };

  const handleDeleteReport = (id: string) => {
    const next = storedReports.filter((r) => r.id !== id);
    writeStoredReports(projectId, next);
    setStoredReports(next);
    notify(tc('status.deleted', 'Gelöscht'), 'success');
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportToPdf('status-report-printable', `Statusreport ${project?.name ?? 'Projekt'}`);
      // Reifegrad Schritt 16 (Status): bei jedem erfolgreichem Export -> level 5
      try { await upsertReifegrad.mutateAsync({ schritt: 16, data: { reifegrad: 5 } }); } catch { /* best-effort */ }
      notify(tc('status.exported', 'PDF erzeugt'), 'success');
    } catch {
      notify(tc('errors.generic', 'Fehler beim Erzeugen des PDF'), 'error');
    } finally {
      setExporting(false);
    }
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h6">{t('statusReport.title', 'Status-Report')}</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSaveReport}
          >
            {t('statusReport.save', 'Bericht speichern')}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? t('statusReport.exporting', 'Erzeuge PDF …') : t('statusReport.exportPdf', 'PDF exportieren')}
          </Button>
        </Stack>
      </Stack>

      <PmCard>
        <Stack spacing={2}>
          <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', display: 'block', mb: 0.5 }}>
                {t('statusReport.template', 'Template')}
              </Typography>
              <PmViewToggle<Audience>
                value={audience}
                onChange={(v) => { setAudience(v); setSections({}); }}
                options={TEMPLATES.map((tp) => ({ value: tp.id, label: tp.label, icon: <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{tp.id.toUpperCase()}</Typography> }))}
              />
            </Box>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t('statusReport.traffic', 'Ampel-Status')}</InputLabel>
              <Select
                label={t('statusReport.traffic', 'Ampel-Status')}
                value={traffic}
                onChange={(e) => setTraffic(e.target.value as TrafficLight)}
              >
                {(['green', 'yellow', 'red'] as TrafficLight[]).map((tl) => (
                  <MenuItem key={tl} value={tl}>{TRAFFIC[tl].label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="date"
              label={t('statusReport.from', 'Von')}
              value={periodFrom}
              onChange={(e) => setPeriodFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label={t('statusReport.to', 'Bis')}
              value={periodTo}
              onChange={(e) => setPeriodTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <PmInfoBox variant="info">
            {template.hint}
          </PmInfoBox>
        </Stack>
      </PmCard>

      {/* Editierbare Sektionen */}
      <PmCard>
        <Stack spacing={2}>
          {template.sections.map((sec) => (
            <Box key={sec.key}>
              <Typography variant="subtitle2" sx={{ color: 'var(--pm-fg-base)', mb: 0.5, fontWeight: 600 }}>
                {sec.label}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={sec.rows}
                placeholder={sec.placeholder}
                value={sections[sec.key] ?? ''}
                onChange={(e) => setSections({ ...sections, [sec.key]: e.target.value })}
              />
            </Box>
          ))}
        </Stack>
      </PmCard>

      {/* Druck-Vorschau (wird von exportToPdf gerastert) */}
      <PmCard>
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', display: 'block', mb: 1 }}>
          {t('statusReport.preview', 'PDF-Vorschau')}
        </Typography>
        <Box
          id="status-report-printable"
          sx={{
            background: '#fff',
            color: '#1D252D',
            padding: 4,
            borderRadius: 1,
            fontFamily: 'Inter, system-ui, sans-serif',
            minHeight: 600,
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="overline" sx={{ color: '#1D8296', letterSpacing: '0.12em', fontWeight: 700 }}>
                  Statusreport · {template.label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1D252D' }}>
                  {project?.name ?? 'Projekt'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#5a6168' }}>
                  Berichtszeitraum: {new Date(periodFrom).toLocaleDateString('de-DE')} – {new Date(periodTo).toLocaleDateString('de-DE')}
                </Typography>
              </Box>
              <Box
                sx={{
                  border: `2px solid ${
                    traffic === 'green' ? '#34D399' : traffic === 'yellow' ? '#FBBF24' : '#F87171'
                  }`,
                  background: traffic === 'green' ? '#ECFDF5' : traffic === 'yellow' ? '#FEF3C7' : '#FEE2E2',
                  color: traffic === 'green' ? '#065F46' : traffic === 'yellow' ? '#92400E' : '#991B1B',
                  padding: '6px 14px',
                  borderRadius: 12,
                  fontSize: '0.85rem',
                  fontWeight: 700,
                }}
              >
                {TRAFFIC[traffic].label}
              </Box>
            </Stack>

            {dash && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 1.5,
                  padding: 1.5,
                  background: '#F3F4F6',
                  borderRadius: 1,
                }}
              >
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>Aufgaben</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1D252D' }}>{dash.tasks.total}</Typography>
                  <Typography variant="caption" sx={{ color: '#5a6168' }}>{dash.tasks.done} erledigt · {dash.tasks.open} offen</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>Risiken</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1D252D' }}>{dash.risks.total}</Typography>
                  <Typography variant="caption" sx={{ color: '#5a6168' }}>{dash.risks.high} kritisch · {dash.risks.mitigated} mitigiert</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>Offene Punkte</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1D252D' }}>{dash.offene_punkte.open}</Typography>
                  <Typography variant="caption" sx={{ color: '#5a6168' }}>{dash.offene_punkte.closed} geschlossen</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>Ø Reifegrad</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1D252D' }}>
                    {dash.reifegrad_avg != null ? dash.reifegrad_avg.toFixed(1) : '—'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#5a6168' }}>0–5 (CMMI)</Typography>
                </Box>
              </Box>
            )}

            <Stack spacing={1.5}>
              {template.sections.map((sec) => (
                <Box key={sec.key}>
                  <Typography variant="subtitle2" sx={{ color: '#1D8296', fontWeight: 700, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem' }}>
                    {sec.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1D252D', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                    {sections[sec.key]?.trim() ? sections[sec.key] : <Box component="span" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>— (leer)</Box>}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Box sx={{ borderTop: '1px solid #E5E7EB', paddingTop: 1, marginTop: 1 }}>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                Generiert am {new Date().toLocaleString('de-DE')} · PM Standalone v0.16
              </Typography>
            </Box>
          </Stack>
        </Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
          <PmPill tone={TRAFFIC[traffic].tone}>{TRAFFIC[traffic].label}</PmPill>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
            Die Vorschau oben wird 1:1 ins PDF gerastert.
          </Typography>
        </Stack>
      </PmCard>

      {/* Historie der gespeicherten Berichte (DEV-only LocalStorage, später Backend-Archiv) */}
      <PmCard>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('statusReport.historyTitle', 'Historie')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
            {t('statusReport.historyHint', 'Lokal im Browser gespeichert · später zentrales Archiv')}
          </Typography>
        </Stack>
        {storedReports.length === 0 ? (
          <PmInfoBox variant="info">
            {t('statusReport.historyEmpty', 'Noch keine Berichte gespeichert. Klick auf „Bericht speichern" oben, um den ersten Eintrag anzulegen.')}
          </PmInfoBox>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>{t('statusReport.col.savedAt', 'Gespeichert am')}</TableCell>
                <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>{t('statusReport.col.audience', 'Template')}</TableCell>
                <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>{t('statusReport.col.traffic', 'Ampel')}</TableCell>
                <TableCell sx={{ color: 'var(--pm-fg-subtle)' }}>{t('statusReport.col.period', 'Zeitraum')}</TableCell>
                <TableCell sx={{ color: 'var(--pm-fg-subtle)' }} align="right">{tc('actions.actions', 'Aktionen')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {storedReports.map((r) => {
                const tmpl = TEMPLATES.find((tp) => tp.id === r.audience);
                return (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {new Date(r.savedAt).toLocaleString('de-DE')}
                    </TableCell>
                    <TableCell>{tmpl?.label ?? r.audience}</TableCell>
                    <TableCell>
                      <PmPill tone={TRAFFIC[r.traffic].tone}>{TRAFFIC[r.traffic].label}</PmPill>
                    </TableCell>
                    <TableCell sx={{ color: 'var(--pm-fg-muted)' }}>
                      {new Date(r.periodFrom).toLocaleDateString('de-DE')} – {new Date(r.periodTo).toLocaleDateString('de-DE')}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('statusReport.load', 'In Editor laden')}>
                        <IconButton size="small" onClick={() => handleLoadReport(r.id)} sx={{ color: 'var(--pm-fg-subtle)' }}>
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={tc('actions.delete', 'Löschen')}>
                        <IconButton size="small" onClick={() => handleDeleteReport(r.id)} sx={{ color: 'var(--pm-error)' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </PmCard>
    </Stack>
  );
}
